import is from '@sindresorhus/is';
import type { StoreApi } from 'zustand';
import { createStore } from 'zustand';

import type { CommentClassification } from './comment-classification';
import type { CommentsScope } from './comments-scope-context';
import { scrollAnchorIntoViewportIfNeeded, scrollOffsetForAncestor } from './scroll-anchor-into-view';
import type { CommentAnchor, CommentTarget } from './types';

export type PendingCommentTarget = {
    readonly target: CommentTarget;
    readonly anchorPageY: number;
    readonly anchorPageBottom: number;
    /**
     * The row element the pending thread was started from. Position consumers must measure it
     * live where possible; `anchorPageY` above is the frozen scroll-invariant capture used when
     * the element has left the document.
     */
    readonly anchorEl: Element | null;
    readonly anchor: CommentAnchor | null;
    readonly commentId: string;
};

export type ActiveThreadState = {
    readonly activeThreadId: string | null;
    readonly commentClassification: CommentClassification | null;
    readonly commentClassificationScope: CommentsScope | null;
    readonly pendingTarget: PendingCommentTarget | null;
    readonly isCommentsOpen: boolean;
    readonly viewMode: CommentsViewMode;
};

export type CommentsViewMode = 'active' | 'hidden';

export type SetPendingTargetParams = {
    readonly target: CommentTarget;
    readonly anchorRect: DOMRect;
    readonly anchorEl: Element | null;
    readonly anchor?: CommentAnchor | null;
    readonly commentId: string;
};

export type ActiveThreadActions = {
    readonly setActiveThreadId: (id: string | null) => void;
    readonly setCommentClassification: (classification: CommentClassification | null, scope?: CommentsScope) => void;
    readonly setPendingTarget: (params: SetPendingTargetParams) => void;
    readonly cancelPending: () => void;
    readonly setIsCommentsOpen: (open: boolean) => void;
    readonly toggleCommentsOpen: () => void;
    readonly setViewMode: (mode: CommentsViewMode) => void;
};

export type ActiveThreadStore = ActiveThreadActions & ActiveThreadState;

type ActiveThreadStoreInitialState = {
    readonly isCommentsOpen?: boolean;
};

export const createActiveThreadStore = (
    initialState: ActiveThreadStoreInitialState = {},
): StoreApi<ActiveThreadStore> =>
    createStore<ActiveThreadStore>((set, get) => ({
        activeThreadId: null,
        commentClassification: null,
        commentClassificationScope: null,
        pendingTarget: null,
        isCommentsOpen: initialState.isCommentsOpen ?? false,
        viewMode: 'active',
        setActiveThreadId: (id) => set({ activeThreadId: id }),
        setCommentClassification: (classification, scope) =>
            set({
                commentClassification: classification,
                commentClassificationScope: is.null(classification) ? null : (scope ?? null),
            }),
        setIsCommentsOpen: (open) => set({ isCommentsOpen: open, viewMode: open ? 'active' : get().viewMode }),
        toggleCommentsOpen: () =>
            set((state) => ({
                isCommentsOpen: !state.isCommentsOpen,
                viewMode: state.isCommentsOpen ? state.viewMode : 'active',
            })),
        setViewMode: (mode) => {
            if (mode === 'hidden') {
                get().cancelPending();
                set({ activeThreadId: null, viewMode: mode });
                return;
            }
            set({ viewMode: mode });
        },
        setPendingTarget: ({ target, anchorRect, anchorEl, anchor, commentId }) => {
            const scrollOffset = scrollOffsetForAncestor(anchorEl);
            set({
                activeThreadId: null,
                isCommentsOpen: true,
                pendingTarget: {
                    target,
                    anchorPageY: anchorRect.top + scrollOffset,
                    anchorPageBottom: anchorRect.bottom + scrollOffset,
                    anchorEl: anchorEl ?? null,
                    anchor: anchor ?? null,
                    commentId,
                },
                viewMode: 'active',
            });
            scrollAnchorIntoViewportIfNeeded(anchorEl, anchorRect);
        },
        cancelPending: () => set({ pendingTarget: null }),
    }));
